import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Calendar, Popover, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    FORMAT_DATE,
    FORMAT_DATE_DISPLAY_DAY,
    FORMAT_DATE_DISPLAY_MONTH,
    FORMAT_DATE_DISPLAY_MONTH_LIST,
    FORMAT_DATE_DISPLAY_YEAR,
    FORMAT_DATE_FOR_SAVE,
} from '../const';
import { getBorderDateTime, getCustomWeekNumber, getEndOfWeek, getStartOfWeek } from '../functions';
import { accessCheck } from '../index';
import { SmxPermissionContext } from '../providers/permissionProvider';
import { ComponentsDispatch, RootState } from '../store';
import { ICalendarState, setterCalendar } from '../store/slices/calendarSlice';
import {
    CalendarScaleType,
    CalendarViewType,
    HeaderType,
    ItemTaskType,
    ItemType,
    MappingItemIdIndexType,
} from '../types';

const ButtonNavigation = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const { userId, permissionListScore } = useContext(SmxPermissionContext);
    const currentDate = useSelector<RootState, string>((state) => state.calendar.currentDate);
    const viewCalendar = useSelector<RootState, CalendarViewType>((state) => state.calendar.viewCalendar);
    const scheduleScale = useSelector<RootState, CalendarScaleType>((state) => state.calendar.scheduleScale);
    const agendaFilter = useSelector<RootState, HeaderType>((state) => state.calendar.agendaFilter);
    const items = useSelector<RootState, ItemType[]>((state) => state.calendar.items);
    const tasks = useSelector<RootState, ItemTaskType[]>((state) => state.calendar.tasks);
    const filteredItems = useSelector<RootState, ItemType[]>((state) => state.calendar.filteredItems);
    const filteredTasks = useSelector<RootState, ItemTaskType[]>((state) => state.calendar.filteredTasks);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>(
        (state) => state.calendar.mappingItemIdIndex,
    );
    const currentDateTime = DateTime.fromISO(currentDate);

    useEffect(() => {
        const filterItems = () => {
            switch (viewCalendar) {
                case 'list':
                    return items.filter(
                        (item) =>
                            currentDateTime.month >= DateTime.fromISO(item.startDate).month &&
                            currentDateTime.month <= DateTime.fromISO(item.endDate).month &&
                            currentDateTime.year === DateTime.fromISO(item.endDate).year,
                    );
                case 'agenda':
                case 'table':
                    return items.filter(
                        (item) =>
                            currentDateTime.year >= DateTime.fromISO(item.startDate).year &&
                            currentDateTime.year <= DateTime.fromISO(item.endDate).year,
                    );
                case 'schedule':
                    switch (scheduleScale) {
                        // case 'day': return items.filter((item) =>
                        //     currentDateTime.toSeconds() >= DateTime.fromISO(item.startDate).toSeconds() &&
                        //     currentDateTime.toSeconds() <= DateTime.fromISO(item.endDate).toSeconds()
                        // )
                        case 'day':
                        case 'week':
                            const startDateWeek = Math.round(getStartOfWeek(currentDateTime).toSeconds());
                            const endDateWeek = Math.round(getEndOfWeek(currentDateTime).toSeconds());

                            return items.filter((item) => {
                                return (
                                    DateTime.fromISO(item.startDate).toSeconds() < endDateWeek &&
                                    DateTime.fromISO(item.endDate).toSeconds() > startDateWeek
                                );
                            });
                        case 'month':
                            return items.filter(
                                (item) =>
                                    currentDateTime.month >= DateTime.fromISO(item.startDate).month - 1 &&
                                    currentDateTime.month <= DateTime.fromISO(item.endDate).month + 1 &&
                                    currentDateTime.year === DateTime.fromISO(item.endDate).year,
                            );
                        case 'year':
                            return items.filter(
                                (item) =>
                                    currentDateTime.year >= DateTime.fromISO(item.startDate).year &&
                                    currentDateTime.year <= DateTime.fromISO(item.endDate).year,
                            );
                    }
            }
        };

        const filterTaskAndItems = (): Partial<ICalendarState> => {
            const { startBorderDate, endBorderDate } = getBorderDateTime(
                viewCalendar,
                currentDateTime,
                agendaFilter,
                scheduleScale,
            );

            const filteredTasks = [];
            const filteredItems: { [key: string]: ItemType } = {};

            for (const task of tasks) {
                const taskDate = DateTime.fromISO(task.datetime).toSeconds();
                if (taskDate >= startBorderDate && taskDate <= endBorderDate) {
                    filteredTasks.push(task);
                    if (task.schedule_item_id) {
                        filteredItems[task.schedule_item_id] = items[mappingItemIdIndex[task.schedule_item_id]];
                    }
                }
            }

            // const filteredItemsList = filterItems() || [];

            // for (const item of filteredItemsList) {
            //     filteredItems[item.id] = item;
            // }
            //
            // const sortFilteredItems = (itemFirst: ItemType, itemSecond: ItemType) => {
            //     if (itemFirst.startDate > itemSecond.startDate) {
            //         return 1;
            //     } else if (itemFirst.startDate === itemSecond.startDate) {
            //         return itemFirst.endDate > itemSecond.endDate ? -1 : 1;
            //     }
            //     return -1;
            // };

            return {
                filteredItems: filterItems()?.filter((item) => {
                    return accessCheck(
                        permissionListScore,
                        ['work-item.view'],
                        item.ownerItemId === userId,
                        item.assignee?.includes(userId),
                    );
                }), //Object.values(filteredItems).sort(sortFilteredItems),
                filteredTasks,
            };
        };

        dispatch(setterCalendar(filterTaskAndItems()));
    }, [currentDate, viewCalendar, items, agendaFilter, mappingItemIdIndex]);

    const getPeriodName = () => {
        switch (viewCalendar) {
            case 'schedule':
                switch (scheduleScale) {
                    case 'day':
                        return 'day';
                    case 'month':
                        return 'month';
                    case 'week':
                        return 'week';

                    default:
                        return 'year';
                }
            case 'table':
            case 'list':
                return 'month';
            case 'agenda':
                return agendaFilter;
        }
    };

    const handleLeft = () => {
        const period = getPeriodName();
        const newCurrentOffsetDay = currentDateTime.minus({ [period]: 1 }).toFormat(FORMAT_DATE);
        dispatch(setterCalendar({ currentDate: newCurrentOffsetDay }));
    };

    const handleRight = () => {
        const period = getPeriodName();
        const newCurrentOffsetDay = currentDateTime.plus({ [period]: 1 }).toFormat(FORMAT_DATE);
        dispatch(setterCalendar({ currentDate: newCurrentOffsetDay }));
    };

    const renderText = () => {
        const weekNumber = getCustomWeekNumber(currentDateTime);
        const format = (stringFormat: string) => currentDateTime.toFormat(stringFormat);

        switch (viewCalendar) {
            case 'schedule':
                switch (scheduleScale) {
                    case 'day':
                        return format(FORMAT_DATE_DISPLAY_DAY);
                    case 'month':
                        return format(FORMAT_DATE_DISPLAY_MONTH);
                    case 'week':
                        return `${format(FORMAT_DATE_DISPLAY_MONTH)} (Week ${weekNumber})`;
                    default:
                        return format(FORMAT_DATE_DISPLAY_YEAR);
                }
            case 'list':
                return `${format(FORMAT_DATE_DISPLAY_MONTH_LIST)}. ${filteredItems.length} Events`;
            case 'agenda': {
                const text = ` ${filteredTasks.length || 'No'} Event${filteredTasks.length === 1 ? '' : 's'}`;
                switch (agendaFilter) {
                    case 'day':
                        return `${format(FORMAT_DATE_DISPLAY_DAY)} ${text}`;
                    case 'month':
                        return `${format(FORMAT_DATE_DISPLAY_MONTH)} ${text}`;
                    case 'week':
                        return `${format(FORMAT_DATE_DISPLAY_MONTH)} (Week ${weekNumber}) ${text}`;
                    case 'year':
                        return `${format(FORMAT_DATE_DISPLAY_YEAR)} ${text}`;
                    case 'quarter':
                        return `${format(FORMAT_DATE_DISPLAY_YEAR)} ${text}`;
                }
            }
        }
    };

    return (
        <Space>
            <Button onClick={handleLeft}>
                <LeftOutlined />
            </Button>
            <Button onClick={handleRight}>
                <RightOutlined />
            </Button>
            <Popover
                content={
                    <div
                        style={{
                            width: 290,
                        }}
                    >
                        <Calendar
                            value={dayjs(currentDate)}
                            onSelect={(date) => {
                                dispatch(
                                    setterCalendar({
                                        currentDate: date.format(FORMAT_DATE_FOR_SAVE),
                                    }),
                                );
                            }}
                            fullscreen={false}
                        />
                    </div>
                }
                placement="bottomRight"
                style={{ width: 50 }}
            >
                <Typography style={{ fontSize: 20, fontWeight: 'bold' }}>{renderText()}</Typography>
            </Popover>
        </Space>
    );
};

export default ButtonNavigation;
